import { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useStore } from "../context/StoreContext";
import {
  Storefront, Globe, CurrencyDollar, Clock, Hash, Receipt, Phone, At, EnvelopeSimple,
  FloppyDisk, IdentificationCard, Warehouse,
} from "@phosphor-icons/react";

const TIMEZONES = [
  "Australia/Brisbane", "Australia/Sydney", "Australia/Melbourne", "Australia/Perth", "Australia/Adelaide", "Australia/Darwin", "Australia/Hobart",
  "Pacific/Auckland",
  "Asia/Singapore", "Asia/Hong_Kong", "Asia/Tokyo", "Asia/Kolkata", "Asia/Dubai",
  "Europe/London", "Europe/Paris", "Europe/Berlin", "Europe/Madrid",
  "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles", "America/Toronto", "America/Vancouver",
  "UTC",
];

const CURRENCIES = [
  { code: "AUD", label: "AUD · Australian Dollar", symbol: "$" },
  { code: "USD", label: "USD · US Dollar", symbol: "$" },
  { code: "NZD", label: "NZD · New Zealand Dollar", symbol: "$" },
  { code: "CAD", label: "CAD · Canadian Dollar", symbol: "$" },
  { code: "GBP", label: "GBP · Pound Sterling", symbol: "£" },
  { code: "EUR", label: "EUR · Euro", symbol: "€" },
  { code: "SGD", label: "SGD · Singapore Dollar", symbol: "$" },
  { code: "INR", label: "INR · Indian Rupee", symbol: "₹" },
  { code: "AED", label: "AED · UAE Dirham", symbol: "د.إ" },
];

const DAYS = [
  { key: "mon", label: "Mon" }, { key: "tue", label: "Tue" }, { key: "wed", label: "Wed" },
  { key: "thu", label: "Thu" }, { key: "fri", label: "Fri" }, { key: "sat", label: "Sat" }, { key: "sun", label: "Sun" },
];

const defaultHours = () => ({
  mon: { open: "09:00", close: "17:00", closed: false },
  tue: { open: "09:00", close: "17:00", closed: false },
  wed: { open: "09:00", close: "17:00", closed: false },
  thu: { open: "09:00", close: "17:00", closed: false },
  fri: { open: "09:00", close: "17:00", closed: false },
  sat: { open: "09:00", close: "14:00", closed: false },
  sun: { open: "", close: "", closed: true },
});

export default function GeneralTab() {
  const { currentStore, refresh } = useStore();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!currentStore) return;
    // Treat empty/uninitialized hours as a fresh install — fill with sensible defaults
    const hasHours = (h) =>
      h && Object.values(h).some((d) => (d?.open || "") || (d?.close || ""));
    setForm({
      name: currentStore.name || "",
      address: currentStore.address || "",
      phone: currentStore.phone || "",
      email: currentStore.email || "",
      website: currentStore.website || "",
      abn: currentStore.abn || "",
      google_review_url: currentStore.google_review_url || "",
      timezone: currentStore.timezone || "Australia/Brisbane",
      currency: currentStore.currency || "AUD",
      tax_rate: currentStore.tax_rate ?? 10,
      tax_inclusive: currentStore.tax_inclusive ?? true,
      invoice_prefix: currentStore.invoice_prefix || "S-",
      ticket_prefix: currentStore.ticket_prefix || "R-",
      default_low_stock: currentStore.default_low_stock ?? 5,
      business_hours: hasHours(currentStore.business_hours) ? currentStore.business_hours : defaultHours(),
    });
    setDirty(false);
  }, [currentStore]);

  if (!currentStore || !form) {
    return <div className="p-8 text-sm text-zinc-500">Loading store settings…</div>;
  }

  const set = (patch) => { setForm((f) => ({ ...f, ...patch })); setDirty(true); };
  const setHour = (day, field, value) => {
    setForm((f) => ({
      ...f,
      business_hours: { ...f.business_hours, [day]: { ...f.business_hours[day], [field]: value } },
    }));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/stores/${currentStore.id}`, form);
      await refresh();
      toast.success("Store settings saved");
      setDirty(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="p-6 lg:p-8 pb-32 space-y-6 max-w-5xl" data-testid="general-tab">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.18em] text-zinc-500 font-semibold mb-1">
            Editing store
          </div>
          <h2 className="font-heading font-bold text-2xl tracking-tight text-[#121216]">
            {currentStore.name}
          </h2>
          <div className="text-sm text-zinc-500 mt-1">
            Changes apply only to the currently-active store. Switch stores in the Stores tab to edit another.
          </div>
        </div>
      </div>

      {/* Business info */}
      <Section icon={Storefront} title="Business info" subtitle="Shown on receipts, invoices, and emails">
        <Grid>
          <Field label="Store name" required>
            <input value={form.name} onChange={(e) => set({ name: e.target.value })} className={inputCls} data-testid="gen-name" />
          </Field>
          <Field label="Phone" icon={Phone}>
            <input value={form.phone} onChange={(e) => set({ phone: e.target.value })} className={inputCls + " font-mono"} placeholder="+61 …" data-testid="gen-phone" />
          </Field>
          <Field label="Email" icon={EnvelopeSimple}>
            <input type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} className={inputCls} placeholder="shop@example.com" data-testid="gen-email" />
          </Field>
          <Field label="Website" icon={Globe}>
            <input value={form.website} onChange={(e) => set({ website: e.target.value })} className={inputCls + " font-mono text-xs"} placeholder="https://…" data-testid="gen-website" />
          </Field>
          <Field label="Address" wide>
            <input value={form.address} onChange={(e) => set({ address: e.target.value })} className={inputCls} placeholder="Street, City, State, Postcode" data-testid="gen-address" />
          </Field>
          <Field label="ABN / Tax ID" icon={IdentificationCard}>
            <input value={form.abn} onChange={(e) => set({ abn: e.target.value })} className={inputCls + " font-mono"} placeholder="12 345 678 901" data-testid="gen-abn" />
          </Field>
          <Field label="Google Review URL" wide>
            <input
              value={form.google_review_url}
              onChange={(e) => set({ google_review_url: e.target.value })}
              placeholder="https://g.page/r/..."
              className={inputCls + " font-mono text-xs"}
              data-testid="gen-review-url"
            />
            <Help>Used by the "SMS Google Review" action. Grab from Google Business → Get more reviews.</Help>
          </Field>
        </Grid>
      </Section>

      {/* Regional */}
      <Section icon={Globe} title="Regional" subtitle="Timezone, currency, and tax defaults">
        <Grid>
          <Field label="Timezone" icon={Clock}>
            <select value={form.timezone} onChange={(e) => set({ timezone: e.target.value })} className={inputCls} data-testid="gen-timezone">
              {TIMEZONES.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
            </select>
            <Help>Controls "Today at the counter" on the Dashboard, bookings display times, and receipt timestamps.</Help>
          </Field>
          <Field label="Currency" icon={CurrencyDollar}>
            <select value={form.currency} onChange={(e) => set({ currency: e.target.value })} className={inputCls} data-testid="gen-currency">
              {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
            </select>
          </Field>
          <Field label="Tax rate (%)" icon={Receipt}>
            <input
              type="number" min="0" max="100" step="0.01"
              value={form.tax_rate}
              onChange={(e) => set({ tax_rate: parseFloat(e.target.value || 0) })}
              className={inputCls + " font-mono"}
              data-testid="gen-tax-rate"
            />
            <Help>Default GST / VAT. Applied automatically when the tax toggle is enabled in a sale.</Help>
          </Field>
          <Field label="Tax pricing mode">
            <div className="rounded-xl border border-slate-200 bg-white p-1 grid grid-cols-2 gap-1">
              <button
                type="button"
                onClick={() => set({ tax_inclusive: true })}
                data-testid="gen-tax-inclusive"
                className={`h-10 rounded-lg text-[12px] font-semibold transition-all ${
                  form.tax_inclusive
                    ? "bg-[#1E5FEB] text-white shadow-sm"
                    : "bg-transparent text-slate-600 hover:bg-slate-100"
                }`}
              >
                TAX INCLUDED
              </button>
              <button
                type="button"
                onClick={() => set({ tax_inclusive: false })}
                data-testid="gen-tax-exclusive"
                className={`h-10 rounded-lg text-[12px] font-semibold transition-all ${
                  !form.tax_inclusive
                    ? "bg-[#1E5FEB] text-white shadow-sm"
                    : "bg-transparent text-slate-600 hover:bg-slate-100"
                }`}
              >
                ADD AT CHECKOUT
              </button>
            </div>
            <Help>
              {form.tax_inclusive
                ? "Prices shown already include tax (e.g. $100 = $90.91 + $9.09 GST). Default for AU/NZ/UK/EU."
                : "Tax is added on top of the price at checkout (e.g. $100 + $10 GST = $110). Default for US/CA."}
            </Help>
          </Field>
        </Grid>
      </Section>

      {/* Numbering & defaults */}
      <Section icon={Hash} title="Numbering & defaults" subtitle="Auto-generated numbers on new tickets and sales">
        <Grid>
          <Field label="Ticket number prefix">
            <div className="flex items-center gap-2">
              <input
                value={form.ticket_prefix}
                onChange={(e) => set({ ticket_prefix: e.target.value })}
                className={inputCls + " font-mono max-w-[120px]"}
                placeholder="R-"
                data-testid="gen-ticket-prefix"
              />
              <span className="font-mono text-sm text-zinc-500">
                {form.ticket_prefix}00007
              </span>
            </div>
          </Field>
          <Field label="Invoice number prefix">
            <div className="flex items-center gap-2">
              <input
                value={form.invoice_prefix}
                onChange={(e) => set({ invoice_prefix: e.target.value })}
                className={inputCls + " font-mono max-w-[120px]"}
                placeholder="S-"
                data-testid="gen-invoice-prefix"
              />
              <span className="font-mono text-sm text-zinc-500">
                {form.invoice_prefix}000123
              </span>
            </div>
          </Field>
          <Field label="Default low-stock threshold" icon={Warehouse}>
            <input
              type="number" min="0" step="1"
              value={form.default_low_stock}
              onChange={(e) => set({ default_low_stock: parseInt(e.target.value || 0, 10) })}
              className={inputCls + " font-mono max-w-[120px]"}
              data-testid="gen-low-stock"
            />
            <Help>Used for new products. Existing products keep their own threshold.</Help>
          </Field>
        </Grid>
      </Section>

      {/* Business hours */}
      <Section icon={Clock} title="Business hours" subtitle="Shown on bookings and the customer-facing pickup page">
        <div className="rounded-xl border border-slate-200 bg-white divide-y divide-slate-100 overflow-hidden">
          {DAYS.map((d) => {
            const h = form.business_hours[d.key] || { open: "", close: "", closed: false };
            return (
              <div key={d.key} className="flex items-center gap-3 px-4 py-2.5" data-testid={`hours-row-${d.key}`}>
                <div className="w-14 text-[12px] font-semibold text-slate-700 uppercase tracking-wider">{d.label}</div>
                <label className="flex items-center gap-1.5 text-[11px] font-semibold text-slate-600 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={!h.closed}
                    onChange={(e) => setHour(d.key, "closed", !e.target.checked)}
                    className="accent-[#1E5FEB] w-4 h-4"
                    data-testid={`hours-open-${d.key}`}
                  />
                  OPEN
                </label>
                {h.closed ? (
                  <span className="text-[12px] text-slate-400 italic ml-2">Closed</span>
                ) : (
                  <div className="flex items-center gap-2 ml-2">
                    <input
                      type="time"
                      value={h.open || ""}
                      onChange={(e) => setHour(d.key, "open", e.target.value)}
                      className="h-9 px-2 rounded-lg border border-slate-200 focus:border-[#1E5FEB] focus:outline-none text-sm font-mono"
                    />
                    <span className="text-slate-400 text-xs">→</span>
                    <input
                      type="time"
                      value={h.close || ""}
                      onChange={(e) => setHour(d.key, "close", e.target.value)}
                      className="h-9 px-2 rounded-lg border border-slate-200 focus:border-[#1E5FEB] focus:outline-none text-sm font-mono"
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </Section>

      {/* Sticky save bar */}
      {dirty && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-slate-900 text-white rounded-full shadow-2xl shadow-slate-900/30 flex items-center gap-4 pl-5 pr-2 py-2 animate-in slide-in-from-bottom duration-200">
          <span className="text-[13px] font-medium">You have unsaved changes</span>
          <button
            onClick={save}
            disabled={saving}
            className="h-9 px-5 rounded-full bg-gradient-to-r from-[#1E5FEB] to-[#1849C8] hover:from-[#1849C8] hover:to-[#123a9e] text-white text-[12px] font-semibold flex items-center gap-1.5 disabled:opacity-60 transition-all shadow-md shadow-blue-500/25"
            data-testid="gen-save-btn"
          >
            <FloppyDisk size={14} weight="bold" /> {saving ? "SAVING…" : "SAVE CHANGES"}
          </button>
        </div>
      )}
    </div>
  );
}

// ---------- helpers ----------
const inputCls =
  "w-full h-11 px-3 rounded-xl border border-slate-200 focus:border-[#1E5FEB] focus:ring-2 focus:ring-blue-100 focus:outline-none text-sm bg-white transition-all";

function Section({ icon: Icon, title, subtitle, children }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <header className="flex items-start gap-3 px-5 py-4 border-b border-slate-100 bg-slate-50/40">
        <div className="w-9 h-9 rounded-xl bg-white border border-slate-200 flex items-center justify-center shrink-0">
          <Icon size={16} weight="duotone" className="text-[#1E5FEB]" />
        </div>
        <div className="min-w-0">
          <h3 className="font-heading font-bold text-[15px] text-slate-900 tracking-tight">{title}</h3>
          {subtitle && <div className="text-[12px] text-slate-500">{subtitle}</div>}
        </div>
      </header>
      <div className="p-5">{children}</div>
    </section>
  );
}

function Grid({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3.5">{children}</div>;
}

function Field({ label, icon: Icon, required, wide, children }) {
  return (
    <label className={`block ${wide ? "sm:col-span-2" : ""}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-1.5">
        {Icon && <Icon size={11} weight="bold" />}
        {label} {required && <span className="text-[#1E5FEB]">*</span>}
      </div>
      {children}
    </label>
  );
}

function Help({ children }) {
  return <div className="text-[11px] text-slate-500 mt-1 leading-relaxed">{children}</div>;
}
